/**
 * Admin Comics list helpers — filter parity, search, summary copy.
 * @see docs/APP_UI_SPEC.md — A3
 */

/**
 * Whether a flattened application row belongs in the comics list under current UI filters.
 * Mirrors `filteredApplications` in AdminComicsPage (blocked, venue, rejected toggle, status).
 *
 * @param {{ email?: string, status?: string|null, venue_id?: number|string|null }} flatApp
 * @param {{
 *   blockedEmails: Set<string>,
 *   selectedVenue: string|number|'all',
 *   selectedStatus: string|'all',
 *   showRejected: boolean,
 * }} opts
 */
export function applicationMatchesAdminComicsFilters(flatApp, opts) {
  const email = flatApp.email?.toLowerCase();
  if (!email || opts.blockedEmails.has(email)) return false;
  if (opts.selectedVenue !== 'all' && flatApp.venue_id != opts.selectedVenue) return false;
  const effectiveStatus = flatApp.status || 'pending';
  if (!opts.showRejected && effectiveStatus === 'rejected') return false;
  if (opts.selectedStatus !== 'all') {
    const eff = flatApp.status || 'pending';
    if (eff !== opts.selectedStatus) return false;
  }
  return true;
}

/**
 * Apply SQL-aligned prefilters on a Supabase query for paginated scans (count / search).
 * Does not apply blocked-email filter — use {@link applicationMatchesAdminComicsFilters} on rows.
 *
 * @param {*} query — Supabase query builder (chainable `.eq` / `.or` / …).
 * @param {{
 *   selectedVenue: string|number|'all',
 *   selectedStatus: string|'all',
 *   showRejected: boolean,
 * }} opts
 */
export function applyAdminComicsSqlPrefilters(query, opts) {
  let q = query;
  if (opts.selectedVenue !== 'all') {
    q = q.eq('events.venue_id', opts.selectedVenue);
  }
  if (opts.selectedStatus !== 'all') {
    if (opts.selectedStatus === 'pending') {
      q = q.or('status.is.null,status.eq.pending');
    } else {
      q = q.eq('status', opts.selectedStatus);
    }
  } else if (!opts.showRejected) {
    q = q.or('status.is.null,status.eq.pending,status.eq.offered,status.eq.confirmed');
  }
  return q;
}

/**
 * Case-insensitive substring filter on grouped comic rows (display name + email).
 *
 * @param {Array<{ comedian_name?: string, email?: string }>} groups
 * @param {string} rawTerm
 * @returns {typeof groups}
 */
export function filterGroupedComicsBySearch(groups, rawTerm) {
  const term = (rawTerm || '').trim().toLowerCase();
  if (!term) return groups;
  return groups.filter(g => {
    const name = (g.comedian_name || '').toLowerCase();
    const em = (g.email || '').toLowerCase();
    return name.includes(term) || em.includes(term);
  });
}

/** Remove ILIKE wildcards from user input (literal substring search). */
export function stripIlikeWildcards(term) {
  return String(term || '').replace(/%/g, '').replace(/_/g, '');
}

/**
 * Header / section summary line for loaded vs total comics.
 *
 * @param {{
 *   loadedGroupCount: number,
 *   totalCount: number|null,
 *   totalLoading?: boolean,
 *   hasMoreData: boolean,
 *   searchTerm: string,
 *   matchingCount: number,
 * }} p
 */
export function buildComicsSummaryLine(p) {
  const search = (p.searchTerm || '').trim();
  const ofTotal =
    p.totalLoading || p.totalCount == null ? '' : ` of ${p.totalCount}`;

  if (search) {
    const base = `Showing ${p.matchingCount} matching "${search}"`;
    if (p.totalLoading || p.totalCount == null) return base;
    return `${base} (${p.totalCount} comics with current filters)`;
  }

  if (p.totalLoading || p.totalCount == null) {
    const hint =
      p.hasMoreData && p.loadedGroupCount > 0
        ? ' — scroll to load more'
        : '';
    return `Showing ${p.loadedGroupCount} comics${hint}`;
  }

  if (!p.hasMoreData && p.loadedGroupCount === p.totalCount) {
    return `Showing all ${p.totalCount} comics`;
  }

  return `Showing ${p.loadedGroupCount}${ofTotal} comics — scroll to see more`;
}
